import React, { useEffect, useState } from 'react';
import { money, MAX_LINE_QUANTITY } from '../shop';
import { productPhoto } from '../photos';
import { ProductPhoto } from './Photo';

const TAGS = ['bestseller', 'classic', 'fruity', 'floral', 'nutty', 'tea', 'seasonal'];
const label = (t) => t.replace('-', ' ');

export function Filters({ filters, setFilters, flavors, count }) {
  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <section className="filters reveal" aria-label="Find macarons">
      <div className="tags" role="group" aria-label="Collections">
        <button type="button" className={`tag ${filters.tag === '' ? 'active' : ''}`} aria-pressed={filters.tag === ''}
          onClick={() => setFilters({ ...filters, tag: '' })}>all</button>
        {TAGS.map((t) => (
          <button key={t} type="button" className={`tag ${filters.tag === t ? 'active' : ''}`} aria-pressed={filters.tag === t}
            onClick={() => setFilters({ ...filters, tag: filters.tag === t ? '' : t })}>{label(t)}</button>
        ))}
      </div>
      <div className="filter-row">
        <label className="search-box">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
          <input className="search" type="search" placeholder="Search flavours, ingredients…" value={filters.q} onChange={set('q')} aria-label="Search" />
        </label>
        <select value={filters.flavor} onChange={set('flavor')} aria-label="Flavor">
          <option value="">All flavours</option>
          {flavors.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filters.sort} onChange={set('sort')} aria-label="Sort">
          <option value="featured">Featured</option>
          <option value="name">Name</option>
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
        </select>
        <label className="toggle"><input type="checkbox" checked={filters.inStock} onChange={set('inStock')} /><span>In stock only</span></label>
        <span className="result-count" aria-live="polite">{count} {count === 1 ? 'flavour' : 'flavours'}</span>
      </div>
    </section>
  );
}

const stockText = (m, inCart) => {
  const left = m.stock - inCart;
  if (m.stock <= 0) return 'Sold out';
  if (left <= 0) return 'All in your box';
  return left <= 12 ? `Only ${left} left` : 'In stock';
};

function Badge({ m }) {
  if (m.stock <= 0) return <span className="badge-pill muted-pill">Sold out</span>;
  if (m.tags.includes('bestseller')) return <span className="badge-pill">Bestseller</span>;
  if (m.tags.includes('seasonal')) return <span className="badge-pill">Seasonal</span>;
  return null;
}

export const productHref = (m) => `#/macaron/${m.slug}`;

// The colour of a product's photo backdrop, and a text colour that reads on it, as CSS custom properties.
const tint = (m) => { const p = productPhoto(m); return { '--bg': p.bg, '--ink': p.ink }; };

export function Grid({ macarons, cart, onAdd }) {
  if (!macarons.length) {
    return (
      <div className="empty-state">
        <p className="empty-title">No macarons match</p>
        <p className="muted">Try another search, flavour or collection.</p>
      </div>
    );
  }
  return (
    <div className="macarons-grid">
      {macarons.map((m, i) => {
        const inCart = cart[m.id] || 0;
        const canAdd = m.stock - inCart > 0 && inCart < MAX_LINE_QUANTITY;
        return (
          <article key={m.id} className={`macaron-card reveal ${m.stock <= 0 ? 'sold-out' : ''}`} style={{ ...tint(m), '--i': i % 4 }}>
            <a className="card-open" href={productHref(m)} aria-label={`About ${m.name}`}>
              <div className="card-media">
                <Badge m={m} />
                <ProductPhoto product={m} />
              </div>
              <div className="card-body">
                <p className="macaron-flavor">{m.flavor}</p>
                <h3 className="macaron-name">{m.name}</h3>
                <p className="macaron-description">{m.description}</p>
              </div>
            </a>
            <div className="card-foot">
              <div>
                <span className="macaron-price">{money(m.priceCents)}</span>
                <span className={`stock ${m.stock - inCart <= 12 ? 'low' : ''}`}>{stockText(m, inCart)}</span>
              </div>
              <button type="button" className="add-btn" disabled={!canAdd} onClick={() => onAdd(m, 1)}
                aria-label={inCart ? `Add another ${m.name} (${inCart} in box)` : `Add ${m.name} to box`}>
                {inCart ? <><span className="in-box">{inCart}</span> Add</> : <>Add <span aria-hidden="true">+</span></>}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

// The bestsellers as big colour panels: photo, name, price, one add button.
export function Spotlight({ macarons, cart, onAdd }) {
  return (
    <div className="spotlight">
      {macarons.slice(0, 3).map((m, i) => {
        const inCart = cart[m.id] || 0;
        const canAdd = m.stock - inCart > 0 && inCart < MAX_LINE_QUANTITY;
        return (
          <article key={m.id} className="spot reveal" style={{ ...tint(m), '--i': i }}>
            <a className="spot-link" href={productHref(m)} aria-label={`About ${m.name}`}>
              <ProductPhoto product={m} />
              <span className="spot-num" aria-hidden="true">0{i + 1}</span>
            </a>
            <div className="spot-info">
              <div>
                <h3>{m.name}</h3>
                <p>{m.description}</p>
              </div>
              <button type="button" className="spot-add" disabled={!canAdd} onClick={() => onAdd(m, 1)} aria-label={`Add ${m.name} to box from favourites`}>
                <span>{money(m.priceCents)}</span><span aria-hidden="true">+</span>
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

// A product's own page (#/macaron/<slug>): big photo on its colour, details, a quantity picker and a few suggestions.
export function ProductPage({ slug, catalog, cart, onAdd }) {
  const m = catalog.find((p) => p.slug === slug);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(0);
  useEffect(() => { setQty(1); setAdded(0); window.scrollTo(0, 0); }, [slug]);

  if (!m) {
    return (
      <section className="product-page narrow">
        <p className="empty-title">We could not find that macaron.</p>
        <a className="btn btn-dark" href="#/">Back to the shop</a>
      </section>
    );
  }
  const inCart = cart[m.id] || 0;
  const left = Math.min(m.stock - inCart, MAX_LINE_QUANTITY - inCart);
  const nuts = m.tags.includes('contains-nuts');
  const related = catalog.filter((p) => p.id !== m.id && p.tags.some((t) => m.tags.includes(t))).slice(0, 4);
  const add = () => { onAdd(m, qty); setAdded(qty); setQty(1); };

  return (
    <section className="product-page" style={tint(m)}>
      <div className="product-layout">
        <div className="product-media">
          <nav className="crumbs" aria-label="Breadcrumb"><a href="#/">Shop</a><span aria-hidden="true">/</span><span>{m.name}</span></nav>
          <Badge m={m} />
          <ProductPhoto product={m} eager alt={`${m.name} macaron`} className="large" />
        </div>
        <div className="product-info">
          <p className="kicker">{m.flavor} macaron</p>
          <h1>{m.name}</h1>
          <p className="product-price">{money(m.priceCents)} <small>each</small></p>
          <p className="product-description">{m.description}. Almond-flour shells, aged egg whites and a generous filling, baked in small batches every morning.</p>
          <ul className="tag-list">{m.tags.filter((t) => t !== 'contains-nuts').map((t) => <li key={t}>{label(t)}</li>)}</ul>

          <div className="buy-box">
            <div className="presets" role="group" aria-label="Quick quantities">
              {[1, 6, 12].map((n) => (
                <button key={n} type="button" className={`preset ${qty === n ? 'active' : ''}`} disabled={left < n} onClick={() => setQty(n)}>
                  {n === 1 ? 'Single' : `${n} pack`}
                </button>
              ))}
            </div>
            <div className="buy-row">
              <span className="stepper large">
                <button type="button" aria-label="One less" disabled={qty <= 1} onClick={() => setQty(qty - 1)}>−</button>
                <span aria-live="polite">{qty}</span>
                <button type="button" aria-label="One more" disabled={qty >= left} onClick={() => setQty(qty + 1)}>+</button>
              </span>
              <button type="button" className="btn btn-dark grow" disabled={left < qty || left <= 0} onClick={add}>
                {left <= 0 ? (m.stock <= 0 ? 'Sold out today' : 'All in your box') : `Add ${qty} to box · ${money(m.priceCents * qty)}`}
              </button>
            </div>
            <p className="muted" role="status">
              {added ? `Added ${added} to your box (${inCart} in box). ` : inCart ? `${inCart} in your box. ` : ''}
              {m.stock <= 0 ? 'Back tomorrow morning.' : `${Math.max(m.stock - inCart, 0)} available today.`}
            </p>
          </div>

          <dl className="facts">
            <div><dt>Ingredients</dt><dd>Almond flour, sugar, egg whites, butter, natural colours and flavourings.</dd></div>
            <div><dt>Allergens</dt><dd>Almonds, eggs, milk{nuts ? ', and other tree nuts' : ''}. Made in a kitchen that handles nuts.</dd></div>
            <div><dt>Keeping</dt><dd>Best within 3 days, chilled. Take them out 20 minutes before eating.</dd></div>
          </dl>
        </div>
      </div>

      {related.length > 0 && (
        <section className="related" aria-label="You may also like">
          <h2 className="display">You may also <em className="script">like</em></h2>
          <Grid macarons={related} cart={cart} onAdd={onAdd} />
        </section>
      )}
    </section>
  );
}
